import { useEffect, useMemo, useRef, useState } from "react";
import { apiClient } from "@lib/apiClient";

export interface PhoneLookupInfo {
  callerName: string | null;
  callerType: string | null;
  lineType: string | null;
}

// Session-level cache: numbers already fetched are never re-requested, and
// every consumer shares one map (lookups only change server-side, monthly).
const cache = new Map<string, PhoneLookupInfo | null>();
let inflight: Promise<void> | null = null;
let pendingPhones = new Set<string>();

async function flushPending(): Promise<void> {
  const phones = [...pendingPhones];
  pendingPhones = new Set();
  if (phones.length === 0) return;
  try {
    const res = await apiClient.post<{ lookups: Record<string, PhoneLookupInfo> }>("/lookup/batch", { phones });
    for (const p of phones) cache.set(p, res.lookups[p] ?? null);
  } catch {
    // Leave uncached so a later mount can retry.
  }
}

/**
 * Cached caller-ID info for a set of phone numbers. Returns a map that fills
 * in as the (batched, deduplicated) POST /lookup/batch responses arrive; the
 * server only ever serves its cache, so this is cheap to call broadly.
 */
export function usePhoneLookups(phones: string[]): Record<string, PhoneLookupInfo> {
  const [, bump] = useState(0);
  const key = useMemo(() => [...new Set(phones)].sort().join(","), [phones]);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const missing = key.split(",").filter((p) => p && !cache.has(p) && !pendingPhones.has(p));
    if (missing.length > 0) {
      for (const p of missing) pendingPhones.add(p);
      // Coalesce all requests in this microtask burst into one batch call.
      inflight = (inflight ?? Promise.resolve()).then(flushPending).then(() => {
        if (mounted.current) bump((n) => n + 1);
      });
    }
    return () => {
      mounted.current = false;
    };
  }, [key]);

  return useMemo(() => {
    const out: Record<string, PhoneLookupInfo> = {};
    for (const p of key.split(",")) {
      const hit = p ? cache.get(p) : null;
      if (hit) out[p] = hit;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, cache.size]);
}

/** Line types that commonly indicate robocall/spam sources. */
export function isLikelySpamLine(lineType: string | null | undefined): boolean {
  return lineType === "nonFixedVoip";
}
